import { useState, useEffect, useRef } from 'react';
import { Check, Pencil } from 'lucide-react';
import RichTextEditor from '../RichTextEditor';

export interface Section7Data {
  confirmationText: string;
  confirmed: boolean;
}

const CONFIRMATION_TEXT = `<h2>7. Bestätigung der Beratungsleistung und Eigenständigkeit der Berichterstellung</h2><p>Hiermit bestätigen sowohl der Berater als auch der Antragsteller, dass dieser Beratungsbericht alle relevanten Beratungsthemen vollständig dokumentiert und keine weiteren Leistungen erbracht wurden. Der Berater versichert zudem, dass der Bericht eigenständig erstellt wurde und die tatsächlich durchgeführten Beratungsleistungen korrekt wiedergibt.</p>`;

// Keine Leerzeilen zwischen den <p>-Tags — Tiptap serialisiert die Vorlage sonst beim ersten
// Rendern kompakt (ohne die Zeilenumbrüche) und meldet das über den Eltern-Kreislauf als
// "Änderung" zurück; der erneut hereingereichte, bereits kompakte Text weicht dann vom rohen
// Literal ab und schaukelt sich endlos zwischen beiden Formen hoch (siehe DEFAULT_PRINCIPLES_HTML
// in Section6Principles.tsx — gleiche Ursache, gefunden 2026-10-12 beim Grundsätze-opt-in).
const DEFAULT_CONFIRMATION_HTML = `${CONFIRMATION_TEXT}<p>&nbsp;</p><p>&nbsp;</p><p>___________________________ &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; ______________________________</p><p>Ort, Datum &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Unterschrift Berater / Beraterin</p><p>&nbsp;</p><p>&nbsp;</p><p>&nbsp;</p><p>___________________________ &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; ______________________________</p><p>Ort, Datum &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Unterschrift Antragsteller / Antragstellerin</p>`;

interface Section7ConfirmationProps {
  darkMode: boolean;
  onDataChange: (data: Section7Data) => void;
  onGenerate: () => void;
  customerData?: {
    name: string;
    ort?: string;
  };
  beraterName?: string;
  initialData?: Section7Data;
}

export function Section7Confirmation({
  darkMode,
  onDataChange,
  onGenerate,
  customerData,
  beraterName,
  initialData,
}: Section7ConfirmationProps) {
  const [confirmationText, setConfirmationText] = useState(
    initialData?.confirmationText || DEFAULT_CONFIRMATION_HTML
  );
  const [confirmed, setConfirmed] = useState(!!initialData?.confirmed);
  const editorContainerRef = useRef<HTMLDivElement>(null);

  const onDataChangeRef = useRef(onDataChange);
  onDataChangeRef.current = onDataChange;

  useEffect(() => {
    onDataChangeRef.current({ confirmationText, confirmed });
  }, [confirmationText, confirmed]);

  /**
   * Der Bericht wird asynchron geladen — beim Mounten steht hier noch der Standard.
   *
   * Ohne diesen Abgleich bleibt die Bestätigung des Nutzers unsichtbar: Der gespeicherte
   * Haken trifft erst NACH dem Mounten ein, der Abschnitt behielt aber seinen Startwert
   * („nicht bestätigt") und meldete ihn per onDataChange nach oben — der geladene Stand war
   * damit wieder weg, und der Fortschritt stand auf 0%, obwohl in der Datenbank
   * `confirmed: true` lag.
   *
   * Übernommen wird nur, was sich von außen WIRKLICH ändert (Vergleich gegen den zuletzt
   * gesehenen Wert). Sonst würde das eigene Zurücksetzen beim Tippen sofort wieder
   * überschrieben.
   */
  const zuletztVonAussen = useRef({ text: initialData?.confirmationText || '', confirmed: !!initialData?.confirmed });
  /** Zuletzt VON AUSSEN gesetzter Text. Der Editor meldet auch programmatische Änderungen
   *  als onChange — ohne diese Merke würde das Nachladen die Bestätigung sofort wieder
   *  entwerten (siehe Textänderung weiter unten). */
  const vonAussenGesetzt = useRef<string | null>(null);
  useEffect(() => {
    const text = initialData?.confirmationText || '';
    const bestaetigt = !!initialData?.confirmed;
    if (text !== zuletztVonAussen.current.text) {
      zuletztVonAussen.current.text = text;
      if (text) { vonAussenGesetzt.current = text; setConfirmationText(text); }
    }
    if (bestaetigt !== zuletztVonAussen.current.confirmed) {
      zuletztVonAussen.current.confirmed = bestaetigt;
      setConfirmed(bestaetigt);
    }
  }, [initialData?.confirmationText, initialData?.confirmed]);

  // Eine Änderung am Text entwertet eine vorherige Bestätigung — die bezog
  // sich auf den alten Wortlaut.
  const handleTextChange = (html: string) => {
    setConfirmationText(html);
    // Nur eine Änderung des Nutzers entwertet die Bestätigung. Der nachgeladene Text ist
    // genau der, der bestätigt wurde — er darf den Haken nicht zurücknehmen.
    if (html === vonAussenGesetzt.current) return;
    setConfirmed(false);
  };

  const handleEditClick = () => {
    const editable = editorContainerRef.current?.querySelector<HTMLElement>('.ProseMirror');
    if (!editable) return;
    editable.focus();
    editable.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <div className={`rounded-lg shadow-sm flex-1 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'} border`}>
      <div className="mx-6 my-6">
        {/* Hinweis */}
        <div className={`mb-5 p-3 rounded-lg text-sm ${
          darkMode ? 'bg-green-900/20 border-green-800 text-green-300' : 'bg-green-50 border-green-200 text-green-700'
        } border flex items-center gap-2`}>
          <Check className="w-4 h-4 flex-shrink-0" />
          <span>Standardtext wird automatisch eingefügt. Sie können ihn direkt bearbeiten und formatieren.</span>
        </div>

        {/* TipTap Editor — Text + Unterschriften */}
        <div ref={editorContainerRef}>
          <RichTextEditor
            content={confirmationText}
            onChange={handleTextChange}
            darkMode={darkMode}
            placeholder="Text eingeben..."
            minHeight="400px"
            baseFontSize="14px"
          />
        </div>

        {/* Kein "Text generieren" Button — stattdessen explizite Bestätigung */}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
            Dieser Abschnitt benötigt keine KI-Generierung
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleEditClick}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border text-sm font-medium shadow-sm transition-colors ${
                darkMode
                  ? 'bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600'
                  : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
              }`}
            >
              <Pencil className="w-4 h-4" />
              Bearbeiten
            </button>
            <button
              type="button"
              onClick={() => setConfirmed(true)}
              disabled={confirmed}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium shadow-sm transition-colors text-white ${
                confirmed ? 'bg-emerald-600/70 cursor-default' : 'bg-emerald-600 hover:bg-emerald-700'
              }`}
            >
              <Check className="w-4 h-4" />
              {confirmed ? 'Bestätigt' : 'Bestätigen'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
